import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const billingStripeWhichLinkGuide: AdminGuide = {
  id: "billing-stripe-which-link",
  title:
    "Billing & Stripe: which link to use (Checkout vs Portal vs Stripe Dashboard)",
  category: "Billing & Stripe",
  audience: ["Support", "Ops", "Finance", "CS"],
  summary:
    "Use the right Stripe flow for the customer’s goal and avoid common test/live and link-sharing mistakes.",
  keywords: [
    "stripe",
    "billing",
    "checkout",
    "customer portal",
    "subscription",
    "trial",
    "test mode",
    "live mode",
    "metadata",
    "organisationId",
    "manual checkout",
  ],
  location: "ExternalTool",
  risk: "high",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.STRIPE_SUBSCRIPTIONS,
    GUIDE_SOURCE_DOCS.ADMIN_SUBSCRIPTION_MGMT,
    GUIDE_SOURCE_DOCS.FRONTEND_PRODUCT_ONBOARDING,
  ],
  warnings: [
    "High risk: stop if you are not confident you have the correct customer and organisation.",
    "Never ask a customer for card details. Use Stripe Checkout or the Stripe Customer Portal.",
    "Treat checkout and portal links as sensitive. Do not paste them into public channels.",
    "Always confirm you are in the correct Stripe mode (Test vs Live) before sending any link.",
  ],
  safetyNotes: [
    "Prefer using Admin tools to generate links so mapping metadata is applied consistently.",
    "Mapping contract: HelloCashflow relies on `subscription.metadata.organisationId` to reconcile Stripe subscriptions to organisations.",
    "If you see mismatched org/subscription mapping, do not “guess”. Escalate with IDs.",
  ],
  whatThisHelpsYouDo: [
    "Pick the correct link (Checkout vs Portal) for the customer’s goal.",
    "Use Admin’s Subscription Dashboard for safe link generation and review.",
    "Use Stripe Dashboard for diagnosis (IDs, status, metadata), not for improvising workflows.",
  ],
  whenToUse: [
    "A customer needs to start a subscription, update payment method, change plan, or cancel.",
    "You need to diagnose a billing issue using Stripe IDs and subscription status.",
  ],
  whenNotToUse: [
    "You are not sure which org the customer belongs to.",
    "You are being asked to make manual billing adjustments or exceptions (escalate to Engineering/Finance).",
  ],
  beforeYouStart: [
    "Organisation name and (ideally) organisationId.",
    "Customer email and/or Stripe customer id (`cus_...`) if available.",
    "The environment you are operating in (Preview vs Production).",
    "A ticket/case reference for any high-risk action.",
  ],
  decisionTable: [
    {
      situation: "Customer wants to start a paid subscription",
      use: "Stripe Checkout (Admin → Subscription Dashboard → Manual Checkout)",
      notes:
        "Confirm product, org, and environment first. Share link only with the customer via an approved channel.",
    },
    {
      situation:
        "Customer wants to update payment method / cancel / manage subscription",
      use: "Stripe Customer Portal (Admin → Subscription Dashboard → Customer Portal icon)",
      notes: "Portal is self-serve. Do not handle payment details yourself.",
    },
    {
      situation: "Internal diagnosis (no customer action needed)",
      use: "Stripe Dashboard",
      notes:
        "Use for lookup and verification: subscription status, metadata, invoice/payment events.",
    },
  ],
  steps: [
    "Identify the correct organisation and customer. If you don’t have organisationId, find it first in Admin.",
    "Confirm environment: Preview vs Production. Do not mix environments.",
    "Open Admin → **Subscription Dashboard** (`/subscription`).",
    "For self-serve account management: open **Subscriptions** tab → use the **Customer Portal** action (person icon) on the correct customer row.",
    "For a one-time checkout link: open **Manual Checkout** tab and select Organisation, User, and Product. Enter your ticket reference and follow the UI confirmation steps to generate the link.",
    "Before sharing any link: confirm Stripe mode (Test vs Live) in Stripe Dashboard and confirm the org/customer is correct.",
    "Share the link only with the customer via an approved channel. Do not paste it into public Slack channels.",
    "If diagnosing: in Stripe Dashboard, inspect the subscription and confirm the mapping contract (`subscription.metadata.organisationId`).",
  ],
  checklists: [
    {
      title: "Test mode vs Live mode safety check",
      items: [
        "In Stripe Dashboard, confirm whether **Test mode** is enabled (toggle).",
        "Confirm the customer/subscription IDs match what you expect for this environment.",
        "If anything looks inconsistent, stop and escalate.",
      ],
    },
  ],
  whatSuccessLooksLike: [
    "You used Checkout for new subscriptions and Portal for self-serve management.",
    "You verified Test vs Live mode before sharing a link.",
    "You captured IDs (organisationId, Stripe customer id, subscription id) for any escalation.",
  ],
  troubleshooting: [
    {
      title: "Subscription status in HelloCashflow doesn’t match Stripe",
      body: [
        "Confirm you are looking at the correct environment (Test vs Live).",
        "Collect: organisationId, Stripe customer id, Stripe subscription id, and the time the customer completed checkout.",
        "Escalate to Engineering (webhooks are the source of truth).",
      ],
    },
    {
      title: "Customer says they paid but cannot access paid features",
      body: [
        "In Stripe Dashboard, confirm the subscription exists and is active/trialing (as appropriate).",
        "Confirm `subscription.metadata.organisationId` is present and matches the org.",
        "Escalate with IDs and timestamps if the mapping is missing or incorrect.",
      ],
    },
    {
      title: "Trial confusion (customer expects paid vs trial)",
      body: [
        "Confirm whether the Stripe subscription is trialing or active.",
        "Confirm which product/price was used and when the checkout session was created.",
        "Escalate if trial length or gating behavior looks inconsistent.",
      ],
    },
  ],
  faqs: [
    {
      title: "Why can’t we just “turn on” access manually?",
      body: [
        "HelloCashflow derives access from Stripe via webhook-driven reconciliation.",
        "Manual changes can create inconsistent state that is hard to debug later.",
        "If an exception is required, escalate with business context and the Stripe/org IDs.",
      ],
    },
  ],
  escalateWhen: [
    "You suspect you are in the wrong Stripe mode (Test vs Live) or the wrong customer/org.",
    "The subscription metadata mapping (`organisationId`) is missing or mismatched.",
    "Checkout completed but HelloCashflow access is not updated after a reasonable time window.",
    "You see multiple subscriptions that appear to map to the same organisation.",
  ],
  relatedGuides: [
    { label: "Onboarding support", guideId: "onboarding-support" },
    { label: "Submitting a bug report", guideId: "bug-reporting" },
  ],
  relatedAdminPaths: [
    { label: "Subscription Dashboard", path: "/subscription" },
    { label: "Organisations", path: "/organisations" },
    { label: "Users", path: "/users" },
  ],
};
